import { ReactElement, useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { Grid, Divider } from '@mui/material'
import { SelectLoadable, FormControl, MillimeterField } from '@ui/forms'
import { getStructuralPlates, getFasteners } from '@queries'
import {
  buildFastenerOptions,
  buildFastenerProducerOptions,
  buildFormatOptions,
  buildMaterialOptions,
  buildPlateTypeOptions,
  buildProducerOptions,
} from './utils'

const PlateLayerForm = (): ReactElement => {
  const { watch, setValue } = useFormContext()
  const producerWatch = watch('producer')
  const plateTypeWatch = watch('plateType')
  const plateMaterialWatch = watch('plateMaterial')
  const plateWatch = watch('layer.plate')
  const fastenerProducerWatch = watch('fastenerProducer')
  const fastenerWatch = watch('layer.fastener')

  const renderProducerOptions = useCallback(
    (data: PlateCrossSection[]) => buildProducerOptions(data),
    [],
  )

  const renderPlateTypeOptions = useCallback(
    (data: PlateCrossSection[]) => buildPlateTypeOptions(data, producerWatch),
    [producerWatch],
  )

  const renderMaterialOptions = useCallback(
    (data: PlateCrossSection[]) => buildMaterialOptions(data, producerWatch, plateTypeWatch),
    [producerWatch, plateTypeWatch],
  )

  const renderFormatOptions = useCallback(
    (data: PlateCrossSection[]) => buildFormatOptions(data, producerWatch, plateMaterialWatch),
    [producerWatch, plateMaterialWatch],
  )

  const renderFastenerProducerOptions = useCallback(
    (data: Fastener[]) => buildFastenerProducerOptions(data),
    [],
  )

  const renderFastenerOptions = useCallback(
    (data: Fastener[]) => buildFastenerOptions(data, fastenerProducerWatch),
    [fastenerProducerWatch],
  )

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <SelectLoadable
          label="Produzent"
          name="producer"
          query={getStructuralPlates}
          onChangeFinished={() => {
            setValue('plateType', '')
            setValue('plateMaterial', '')
            setValue('layer.plate', null)
          }}
          renderOptions={renderProducerOptions}
          data-cy="producer"
        />
      </Grid>
      <Grid item xs={12}>
        <SelectLoadable
          label="Plattentyp"
          name="plateType"
          disabled={!producerWatch}
          query={getStructuralPlates}
          onChangeFinished={() => {
            setValue('plateMaterial', '')
            setValue('layer.plate', null)
          }}
          renderOptions={renderPlateTypeOptions}
          data-cy="plate-type"
        />
      </Grid>
      <Grid item xs={12}>
        <SelectLoadable
          label="Material"
          name="plateMaterial"
          disabled={!plateTypeWatch}
          query={getStructuralPlates}
          onChangeFinished={() => setValue('layer.plate', null)}
          renderOptions={renderMaterialOptions}
          data-cy="plate-material"
        />
      </Grid>
      <Grid item xs={12}>
        <SelectLoadable
          label="Format"
          name="layer.plate"
          disabled={!plateMaterialWatch}
          query={getStructuralPlates}
          renderOptions={renderFormatOptions}
          data-cy="plate-format"
        />
      </Grid>
      <Grid item xs={6}>
        <FormControl label="Material Breite">
          <MillimeterField
            name=""
            staticValue={plateWatch ? plateWatch?.material.thickness : ''}
            size="small"
            disabled
            numeralFormatter="0.[0]" // 1 decimal
          />
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item xs={12}>
        <SelectLoadable
          label="Klammer Produzent"
          name="fastenerProducer"
          query={getFasteners}
          onChangeFinished={() => setValue('layer.fastener', null)}
          renderOptions={renderFastenerProducerOptions}
          data-cy="fastener-producer"
        />
      </Grid>
      <Grid item xs={12}>
        <SelectLoadable
          label="Klammer"
          name="layer.fastener"
          disabled={!fastenerProducerWatch}
          query={getFasteners}
          renderOptions={renderFastenerOptions}
          data-cy="fastener"
        />
      </Grid>
      <Grid item xs={6}>
        <FormControl label="Klammer Durchmesser">
          <MillimeterField
            name=""
            staticValue={fastenerWatch ? fastenerWatch.diameter : ''}
            size="small"
            disabled
            numeralFormatter="0.[00]" // 2 decimals
          />
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <FormControl label="Klammer Länge">
          <MillimeterField
            name=""
            staticValue={fastenerWatch ? fastenerWatch.length : ''}
            size="small"
            disabled
            numeralFormatter="0" // 0 decimals
          />
        </FormControl>
      </Grid>
      <Grid item xs={6}>
        <MillimeterField
          label="Klammer Abstand"
          name="layer.fastener_distance"
          data-cy="fastener_distance"
          numeralFormatter="0" // 0 decimals
        />
      </Grid>
    </Grid>
  )
}

export default PlateLayerForm
